import React, { useState, useEffect } from "react";
import {
  FaTimes,
  FaCar,
  FaMapMarkerAlt,
  FaUser,
  FaPhone,
  FaCalendar,
  FaDollarSign,
  FaClock,
  FaRoute,
  FaStar,
  FaCheckCircle,

  FaKey,
  FaSpinner,
  FaMapPin,
  FaUserTie,
  FaRupeeSign
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";
import { formatDuration } from "../utils/formatDuration";

const NEARBY_DRIVER_CALL_STATUS_OPTIONS = [
  "Not Answered",
  "Number Not Valid",
  "Not Interested",
  "Agreed",
  "Far from His Location",
  "Not from His Location",
  "His is Not This Location",
  "Less Fare",
  "Custom Field",
];

const getExotelStatusDisplay = (rawStatus) => {
  const normalized = String(rawStatus || "").trim().toLowerCase();

  if (!normalized) {
    return {
      label: "Not Available",
      className: "text-gray-600 bg-gray-100",
    };
  }

  switch (normalized) {
    case "completed":
      return { label: "Completed", className: "text-green-700 bg-green-100" };
    case "failed":
      return { label: "Failed", className: "text-red-700 bg-red-100" };
    case "busy":
      return { label: "Busy", className: "text-amber-700 bg-amber-100" };
    case "no-answer":
      return { label: "No Answer", className: "text-slate-700 bg-slate-100" };
    default:
      return {
        label: String(rawStatus).trim(),
        className: "text-gray-700 bg-gray-100",
      };
  }
};

const RideDetailModal = ({ show, ride, onClose, onDriverAssigned, onRideUpdated }) => {
  const [driverMobile, setDriverMobile] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [otps, setOtps] = useState(null);
  const [otpsLoading, setOtpsLoading] = useState(false);
  const [nearbyDrivers, setNearbyDrivers] = useState(null);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [nearbyDriverStatusSaving, setNearbyDriverStatusSaving] = useState(null);
  const [customTextByDriver, setCustomTextByDriver] = useState({});
  const [declineReasons, setDeclineReasons] = useState(null);
  const [declineReasonsLoading, setDeclineReasonsLoading] = useState(false);

  // Fetch OTPs when modal is open and ride status is Accepted or Started
  useEffect(() => {
    if (!show || !ride) return;
    const status = ride.status;
    const shouldFetchOtps = status === "Accepted" || status === "Started" || status === "Arrived";
    const customerPhone = ride.userId?.phone || ride.userId?.phoneNumber;
    if (!shouldFetchOtps || !customerPhone) {
      setOtps(null);
      return;
    }
    const fetchOtps = async () => {
      setOtpsLoading(true);
      setOtps(null);
      try {
        const token = localStorage.getItem("adminToken");
        const response = await axiosInstance.post(
          API.ADMIN.RIDE_GET_OTPS,
          { customerPhone: String(customerPhone).replace(/\D/g, "").slice(-10) },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.success && response.data?.data?.otps) {
          setOtps(response.data.data.otps);
        }
      } catch (err) {
        console.error("Error fetching OTPs:", err);
      } finally {
        setOtpsLoading(false);
      }
    };
    fetchOtps();
  }, [show, ride?.userId?.phone, ride?.userId?.phoneNumber, ride?.status, ride?._id]);

  // Fetch nearby drivers when modal is open and ride status is Requested or Planned
  useEffect(() => {
    if (!show || !ride) return;
    const status = ride.status;
    const shouldFetchNearby = status === "Requested" || status === "Planned";
    const coords = ride.pickup?.coordinates;
    const coordArray = Array.isArray(coords) ? coords : coords?.coordinates;
    const lng = coordArray?.[0];
    const lat = coordArray?.[1];
    const carType = ride?.carType;
    const tripType = ride?.tripType;
    if (!shouldFetchNearby || lat == null || lng == null || !carType || !tripType) {
      setNearbyDrivers(null);
      return;
    }
    const fetchNearby = async () => {
      setNearbyLoading(true);
      setNearbyDrivers(null);
      try {
        const token = localStorage.getItem("adminToken");
        const params = new URLSearchParams({
          lat: String(lat),
          lng: String(lng),
          carType: String(carType),
          tripType: String(tripType),
          radius: "10",
        });
        const response = await axiosInstance.get(`${API.ADMIN.DRIVERS_NEARBY}?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data?.success && response.data?.data) {
          setNearbyDrivers(response.data.data);
        }
      } catch (err) {
        console.error("Error fetching nearby drivers:", err);
      } finally {
        setNearbyLoading(false);
      }
    };
    fetchNearby();
  }, [show, ride?.status, ride?.pickup?.coordinates, ride?.carType, ride?.tripType, ride?._id]);

  // Fetch driver decline reasons when modal is open
  useEffect(() => {
    if (!show || !ride?._id) {
      setDeclineReasons(null);
      return;
    }
    const fetchDeclineReasons = async () => {
      setDeclineReasonsLoading(true);
      setDeclineReasons(null);
      try {
        const token = localStorage.getItem("adminToken");
        const response = await axiosInstance.get(
          API.ADMIN.RIDE_DECLINE_REASONS(ride._id),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.success) {
          setDeclineReasons(response.data.declinedReasons || []);
        }
      } catch (err) {
        console.error("Error fetching decline reasons:", err);
      } finally {
        setDeclineReasonsLoading(false);
      }
    };
    fetchDeclineReasons();
  }, [show, ride?._id]);

  if (!show || !ride) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "text-green-600 bg-green-100";
      case "Cancelled":
        return "text-red-600 bg-red-100";
      case "Started":
        return "text-blue-600 bg-blue-100";
      case "Accepted":
        return "text-yellow-600 bg-yellow-100";
      case "Requested":
        return "text-purple-600 bg-purple-100";
      case "Planned":
        return "text-indigo-600 bg-indigo-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const handleAssignDriver = async () => {
    // Validate mobile number (10 digits)
    if (!/^\d{10}$/.test(driverMobile)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    try {
      setAssigning(true);
      const token = localStorage.getItem("adminToken");

      const response = await axiosInstance.post(
        `${API.ADMIN.RIDE_UPDATE_STATUS}/assign`,
        {
          rideId: ride._id,
          driverPhone: driverMobile
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        const smsMessage = response.data.data?.smsMessage || "SMS sent successfully";
        toast.success(`Driver assigned successfully! ${smsMessage}`);
        setDriverMobile("");
        // Call the callback to refresh the ride data
        if (onDriverAssigned) {
          onDriverAssigned();
        }
      } else {
        toast.error(response.data.message || "Failed to assign driver");
      }
    } catch (error) {
      console.error("Error assigning driver:", error);
      toast.error(
        error.response?.data?.message || "Failed to assign driver"
      );
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <FaCar className="text-blue-600 mr-3 text-xl" />
            <div>
              <h3 className="text-xl font-bold text-gray-900">Ride Details</h3>
              <p className="text-sm text-gray-500">Ride ID: {ride._id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Basic Info */}
          <div className="space-y-6">
            {/* Ride Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <FaCar className="mr-2 text-blue-500" />
                Ride Information
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Car Type</p>
                  <p className="font-medium">{ride.carType || "N/A"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Transmission</p>
                  <p className="font-medium">{ride.transmission || "N/A"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Trip Type</p>
                  <p className="font-medium">{ride.tripType || "N/A"}</p>
                </div>
                <div>
                  {/* <p className="text-gray-500">Category</p>
                  <p className="font-medium">{ride.rideCategory || "N/A"}</p> */}
                </div>
                <div>
                  <p className="text-gray-500">Estimated Distance</p>
                  <p className="font-medium">
                    {(Number(ride.estimatedDistance) || 0).toFixed(1)}km
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Final Distance</p>
                  <p className="font-medium">
                    {(Number(ride.actualDistance) || 0).toFixed(1)}km
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Estimated Duration</p>
                  <p className="font-medium">
                    {formatDuration(ride.estimatedTime)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Final Duration</p>
                  <p className="font-medium">
                    { ride.status === "Completed" ? formatDuration(ride.finalDuration || 0) : "0m"}
                    {/* {String(ride.status || "").toLowerCase() === "completed"
                      ? formatDuration(ride.finalDuration)
                      : formatDuration(ride.actualEstimatedTime || 0)} */}
                  </p>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Status</h4>
              <div className="flex items-center mb-3">
                <span
                  className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(
                    ride.status
                  )}`}
                >
                  {ride.status}
                </span>
                {ride.scheduleType && (
                  <span className="ml-2 text-sm text-gray-500">
                    ({ride.scheduleType})
                  </span>
                )}
              </div>
              {ride.status === "Cancelled" && ride.cancellationReason && (
                <div className="mb-3">
                  <p className="text-sm text-red-600 font-medium">
                    Reason: {ride.cancellationReason}
                  </p>
                </div>
              )}
              <div className="text-sm text-gray-600">
                <p>Created: {formatDate(ride.createdAt)}</p>
                {ride.scheduledAt && (
                  <p>Scheduled: {formatDate(ride.scheduledAt)}</p>
                )}
                {ride.acceptedAt && (
                  <p>Accepted: {formatDate(ride.acceptedAt)}</p>
                )}
                {ride.startedAt && <p>Started: {formatDate(ride.startedAt)}</p>}
                {ride.completedAt && (
                  <p>Completed: {formatDate(ride.completedAt)}</p>
                )}
                {ride.cancelledAt && (
                  <p>Cancelled: {formatDate(ride.cancelledAt)}</p>
                )}
              </div>
            </div>

            {/* Payment Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <FaRupeeSign className="mr-2 text-green-500" />
                Payment Information
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Estimated Fare</p>
                  <p className="font-medium">₹{ride.estimatedFare || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">Final Fare</p>
                  {/* if ride status is Completed, show the final fare other wise show 0*/}
                  <p className="font-medium">
                    ₹{ ride.status === "Completed" ? ride.fare : 0}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Driver Earnings</p>
                  <p className="font-medium">
                    ₹{(() => {
                      const net = (ride.driverEarnings || 0) - (ride.totalCommission || 0);
                      return net < 0 ? (ride.driverEarnings || 0) : net;
                    })()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Due Status</p>
                  <p
                    className={`font-medium ${ride.dueStatus === "Paid"
                      ? "text-green-600"
                      : ride.dueStatus === "Failed"
                        ? "text-red-600"
                        : "text-yellow-600"
                      }`}>
                    {ride.dueStatus || "Pending"}
                  </p>
                </div>

                {/* <div>
                  <p className="text-gray-500">Payment Status</p>
                  <p
                    className={`font-medium ${ride.paymentStatus === "Paid"
                      ? "text-green-600"
                      : ride.paymentStatus === "Failed"
                        ? "text-red-600"
                        : "text-yellow-600"
                      }`}
                  >
                    {ride.paymentStatus || "Pending"}
                  </p>
                </div> */}

                {/* <div>
                  <p className="text-gray-500">Method</p>
                  <p className="font-medium">{ride.paymentMethod || "N/A"}</p>
                </div> */}

                {ride.commission && (
                  <>
                    <div>
                      {/* <p className="text-gray-500">Commission</p> */}
                      <p className="text-gray-500">Platform Fee (Commission)</p>
                      <p className="font-medium">
                        ₹{ride.commission} ({ride.commissionPercentage || 0}%)
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">GST on Commission</p>
                      <p className="font-medium">₹{ride.gstAmount || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Total Due</p>
                      <p className="font-medium">₹{ride.totalCommission || 0}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - People & Locations */}
          <div className="space-y-6">
            {/* Customer Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <FaUser className="mr-2 text-blue-500" />
                Customer Information
              </h4>
              {ride.userId ? (
                <div className="space-y-2">
                  <div className="flex items-center">
                    <FaUser className="text-gray-400 mr-2" />
                    <span className="font-medium">{ride.userId.fullName}</span>
                  </div>
                  <div className="flex items-center">
                    <FaPhone className="text-gray-400 mr-2" />
                    <span className="text-sm">{ride.userId.phone}</span>
                  </div>
                  {ride.userId.emailId && (
                    <div className="text-sm text-gray-600">
                      Email: {ride.userId.emailId}
                    </div>
                  )}
                  {ride.userId.city && (
                    <div className="text-sm text-gray-600">
                      City: {ride.userId.city}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">
                  Customer information not available
                </p>
              )}
            </div>

            {/* Driver Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <FaCar className="mr-2 text-green-500" />
                Driver Information
              </h4>
              {ride.driverId ? (
                <div className="space-y-2">
                  <div className="flex items-center">
                    <FaUser className="text-gray-400 mr-2" />
                    <span className="font-medium">
                      {ride.driverId.fullName}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <FaPhone className="text-gray-400 mr-2" />
                    <span className="text-sm">{ride.driverId.phone}</span>
                  </div>
                  {ride.driverId.vehicle && (
                    <div className="text-sm text-gray-600">
                      Vehicle: {ride.driverId.vehicle.make}{" "}
                      {ride.driverId.vehicle.model}
                    </div>
                  )}
                  {ride.driverId.vehicleNumber && (
                    <div className="text-sm text-gray-600">
                      Number: {ride.driverId.vehicleNumber}
                    </div>
                  )}
                  {ride.driverId.city && (
                    <div className="text-sm text-gray-600">
                      City: {ride.driverId.city}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-gray-500 mb-3">Driver not assigned</p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile No
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Enter 10-digit mobile number"
                        className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={driverMobile}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 10) {
                            setDriverMobile(value);
                          }
                        }}
                        maxLength={10}
                      />
                      <button
                        onClick={handleAssignDriver}
                        disabled={assigning || driverMobile.length !== 10}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        {assigning ? "sending sms..." : "Send sms"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Estimated Route Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <FaRoute className="mr-2 text-purple-500" />
                Estimated Route Information
              </h4>
              <div className="space-y-3">
                <div>
                  <div className="flex items-start mb-2">
                    <FaMapMarkerAlt className="text-green-500 mr-2 mt-1" />
                    <div>
                      <p className="font-medium text-sm">Pickup Location</p>
                      <p className="text-sm text-gray-600">
                        {ride.pickup?.address || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-start">
                    <FaMapMarkerAlt className="text-red-500 mr-2 mt-1" />
                    <div>
                      <p className="font-medium text-sm">Dropoff Location</p>
                      <p className="text-sm text-gray-600">
                        {ride.dropoff?.address || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
                {ride.dropoffDate && (
                  <div>
                    <div className="flex items-start">
                      <FaCalendar className="text-blue-500 mr-2 mt-1" />
                      <div>
                        <p className="font-medium text-sm">Dropoff Date</p>
                        <p className="text-sm text-gray-600">
                          {formatDate(ride.dropoffDate)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Route Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <FaRoute className="mr-2 text-purple-500" />
                Final Route Information
              </h4>
              <div className="space-y-3">
                <div>
                  <div className="flex items-start mb-2">
                    <FaMapMarkerAlt className="text-green-500 mr-2 mt-1" />
                    <div>
                      <p className="font-medium text-sm">Pickup Location</p>
                      <p className="text-sm text-gray-600">
                        {ride.driverPickupLocation?.address || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-start">
                    <FaMapMarkerAlt className="text-red-500 mr-2 mt-1" />
                    <div>
                      <p className="font-medium text-sm">Dropoff Location</p>
                      <p className="text-sm text-gray-600">
                        {ride.driverDropoffLocation?.address || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
                {ride.dropoffDate && (
                  <div>
                    <div className="flex items-start">
                      <FaCalendar className="text-blue-500 mr-2 mt-1" />
                      <div>
                        <p className="font-medium text-sm">Dropoff Date</p>
                        <p className="text-sm text-gray-600">
                          {formatDate(ride.dropoffDate)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Nearby Drivers - show when status is Requested or Planned */}
            {(ride.status === "Requested" || ride.status === "Planned") && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3 gap-2">
                  <h4 className="font-semibold text-gray-900 flex items-center">
                    <FaMapPin className="mr-2 text-green-500" />
                    Nearby Drivers
                    {nearbyDrivers?.searchRadiusKm != null && (
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        (within {nearbyDrivers.searchRadiusKm} km)
                      </span>
                    )}
                  </h4>
                </div>
                {nearbyLoading ? (
                  <div className="flex items-center text-gray-500 text-sm">
                    <FaSpinner className="animate-spin mr-2" />
                    Finding nearby drivers...
                  </div>
                ) : nearbyDrivers?.drivers?.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 mb-2">
                      {(nearbyDrivers.totalDriversInRadius ?? nearbyDrivers.totalDrivers ?? nearbyDrivers.driversReturned ?? nearbyDrivers.drivers?.length ?? 0)} driver(s) found near pickup location
                    </p>
                    <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 overflow-hidden bg-white">
                      {nearbyDrivers.drivers.map((d, i) => {
                        const driverIdStr = d.driverId ? String(d.driverId) : "";
                        const saved = (ride.nearbyDriverCallStatuses || []).find(
                          (s) =>
                            (s.driverId && String(s.driverId) === driverIdStr) ||
                            (s.driverId?._id && String(s.driverId._id) === driverIdStr)
                        );
                        const currentStatus = saved?.status || "";
                        const isCustomField = currentStatus === "Custom Field";
                        const customText = customTextByDriver[driverIdStr] ?? saved?.customText ?? "";
                        const saving = nearbyDriverStatusSaving === driverIdStr;
                        const exotelCallEntries = Array.isArray(ride.driverCallStatuses)
                          ? ride.driverCallStatuses.filter((entry) => {
                            const entryDriverId = entry?.driverId?._id || entry?.driverId;
                            return entryDriverId && String(entryDriverId) === driverIdStr;
                          })
                          : [];
                        const latestExotelEntry =
                          exotelCallEntries.length > 0
                            ? exotelCallEntries[exotelCallEntries.length - 1]
                            : null;
                        const exotelStatusDisplay = getExotelStatusDisplay(
                          latestExotelEntry?.callStatus
                        );

                        const saveNearbyDriverStatus = async (status, customTextVal = "") => {
                          if (!ride?._id || !driverIdStr) return;
                          setNearbyDriverStatusSaving(driverIdStr);
                          try {
                            const token = localStorage.getItem("adminToken");
                            const res = await axiosInstance.put(
                              `${API.ADMIN.RIDE_UPDATE_STATUS}/${ride._id}/nearby-driver-call-status`,
                              {
                                driverId: driverIdStr,
                                status: status || "Not Answered",
                                customText: status === "Custom Field" ? customTextVal : "",
                              },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            if (res.data?.success && res.data?.data?.ride && typeof onRideUpdated === "function") {
                              onRideUpdated(res.data.data.ride);
                            }
                          } catch (err) {
                            console.error("Error saving nearby driver call status:", err);
                            toast.error(err.response?.data?.message || "Failed to save status");
                          } finally {
                            setNearbyDriverStatusSaving(null);
                          }
                        };

                        return (
                          <div
                            key={d.driverId || i}
                            // className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 hover:bg-gray-50"
                            className=" px-4 py-3 hover:bg-gray-50"
                          >
                            <div className="flex items-center justify-between min-w-0">
                              <div className="flex items-center">
                                <FaUserTie className="text-blue-500 mr-3 flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {d.name || "N/A"}
                                  </p>
                                  <p className="text-xs text-gray-600 flex items-center mt-0.5">
                                    <FaPhone className="mr-1 text-gray-400" />
                                    {d.phone || "—"}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center flex-shrink-0 ml-3">
                                <FaMapPin className="text-green-500 mr-1 text-xs" />
                                <span className="text-sm font-semibold text-green-600">
                                  {typeof d.distanceKm === "number"
                                    ? `${d.distanceKm.toFixed(1)} km`
                                    : d.distanceKm != null
                                      ? `${d.distanceKm} km`
                                      : "—"}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col pt-2 items-center gap-2 flex-shrink-0 min-w-0">
                              <span className="text-sm font-semibold text-black-600">Call Status By Team</span>
                            </div>
                            <div className="flex flex-col pt-2 items-center gap-2 flex-shrink-0 min-w-0">
                              <select
                                value={currentStatus}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === "Custom Field") {
                                    saveNearbyDriverStatus("Custom Field", customText);
                                  } else {
                                    saveNearbyDriverStatus(v, "");
                                    setCustomTextByDriver((prev) => ({ ...prev, [driverIdStr]: "" }));
                                  }
                                }}
                                disabled={saving}
                                className="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white text-gray-700 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full"
                              >
                                <option value="">— Status —</option>
                                {NEARBY_DRIVER_CALL_STATUS_OPTIONS.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                              {isCustomField && (
                                <input
                                  type="text"
                                  value={customText}
                                  onChange={(e) => setCustomTextByDriver((prev) => ({ ...prev, [driverIdStr]: e.target.value }))}
                                  onBlur={() => saveNearbyDriverStatus("Custom Field", customText)}
                                  placeholder="Custom text"
                                  className="text-xs border border-gray-300 rounded px-2 py-1.5 w-32 min-w-0 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full"
                                />
                              )}
                              {saving && <FaSpinner className="animate-spin text-gray-400 text-xs" />}
                            </div>
                            <div className="flex flex-col pt-2 items-center gap-2 flex-shrink-0 min-w-0">
                              <span className="text-sm font-semibold text-black-600">Call Status By Exotel</span>
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${exotelStatusDisplay.className}`}
                              >
                                {exotelStatusDisplay.label}
                              </span>
                            </div>

                            {/* <div className="flex items-center flex-shrink-0 ml-3">
                              <FaMapPin className="text-green-500 mr-1 text-xs" />
                              <span className="text-sm font-semibold text-green-600">
                                {typeof d.distanceKm === "number"
                                  ? `${d.distanceKm.toFixed(1)} km`
                                  : d.distanceKm != null
                                  ? `${d.distanceKm} km`
                                  : "—"}
                              </span>
                            </div> */}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    No drivers found within the search radius.
                  </p>
                )}
              </div>
            )}

            {/* OTPs - show when status is Accepted or Started */}
            {(ride.status === "Accepted" || ride.status === "Started" || ride.status === "Arrived") && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <FaKey className="mr-2 text-amber-500" />
                  Ride OTPs
                </h4>
                {otpsLoading ? (
                  <div className="flex items-center text-gray-500 text-sm">
                    <FaSpinner className="animate-spin mr-2" />
                    Loading OTPs...
                  </div>
                ) : otps ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                        Start OTP
                      </p>
                      <p className="text-2xl font-bold text-amber-600 tracking-widest">
                        {otps.startOTP || "—"}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                        End OTP
                      </p>
                      <p className="text-2xl font-bold text-amber-600 tracking-widest">
                        {otps.endOTP || "—"}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No active OTPs available.</p>
                )}
              </div>
            )}

            {/* Ratings */}
            {(ride.userRating || ride.driverRating) && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <FaStar className="mr-2 text-yellow-500" />
                  Ratings & Reviews
                </h4>
                <div className="space-y-3">
                  {ride.userRating && (
                    <div>
                      <div className="flex items-center mb-1">
                        <span className="text-sm font-medium">
                          Customer Rating:
                        </span>
                        <div className="flex items-center ml-2">
                          <FaStar className="text-yellow-500" />
                          <span className="ml-1 font-bold">
                            {ride.userRating.value}/5
                          </span>
                        </div>
                      </div>
                      {ride.userRating.feedback && (
                        <p className="text-sm text-gray-600 italic">
                          "{ride.userRating.feedback}"
                        </p>
                      )}
                    </div>
                  )}
                  {ride.driverRating && (
                    <div>
                      <div className="flex items-center mb-1">
                        <span className="text-sm font-medium">
                          Driver Rating:
                        </span>
                        <div className="flex items-center ml-2">
                          <FaStar className="text-yellow-500" />
                          <span className="ml-1 font-bold">
                            {ride.driverRating.value}/5
                          </span>
                        </div>
                      </div>
                      {ride.driverRating.feedback && (
                        <p className="text-sm text-gray-600 italic">
                          "{ride.driverRating.feedback}"
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Timeline */}
        {ride.timeline && ride.timeline.length > 0 && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
              <FaClock className="mr-2 text-blue-500" />
              Ride Timeline
            </h4>
            <div className="space-y-3">
              {ride.timeline.map((event, index) => (
                <div key={index} className="flex items-center">
                  <div
                    className={`w-3 h-3 rounded-full mr-3 ${event.completed ? "bg-green-500" : "bg-gray-300"
                      }`}
                  ></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {event.status}
                      </span>
                      <span className="text-xs text-gray-500">
                        {event.timestamp
                          ? formatDate(event.timestamp)
                          : "Pending"}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3">
                    {event.completed ? (
                      <FaCheckCircle className="text-green-500" />
                    ) : (
                      <FaClock className="text-gray-400" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Driver Decline Reasons */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-4">
            Driver Decline Reasons
            {Array.isArray(declineReasons) && declineReasons.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({declineReasons.length} declined)
              </span>
            )}
          </h4>
          {declineReasonsLoading ? (
            <div className="flex items-center text-sm text-gray-500">
              <FaSpinner className="animate-spin mr-2" />
              Loading decline reasons...
            </div>
          ) : Array.isArray(declineReasons) && declineReasons.length > 0 ? (
            <div className="space-y-2">
              {declineReasons.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-sm"
                >
                  <div>
                    <span className="font-medium">
                      {item.driverName || "Unknown Driver"}
                    </span>
                    {item.driverPhone && (
                      <span className="text-gray-500 ml-1">
                        ({item.driverPhone})
                      </span>
                    )}
                    <span className="text-gray-500 ml-2">declined</span>
                    <span className="text-red-600 ml-1">
                      ({item.reason === "Other" && item.otherReason
                        ? item.otherReason
                        : item.reason || "N/A"})
                    </span>
                  </div>
                  <span className="text-gray-500">
                    {formatDate(item.declinedAt)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No drivers declined yet</p>
          )}
        </div>

        {/* Driver Actions History */}
        {ride.driverActions && ride.driverActions.length > 0 && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-4">
              Driver Actions History
            </h4>
            <div className="space-y-2">
              {ride.driverActions.map((action, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-sm"
                >
                  <div>
                    <span className="font-medium">
                      {action.driverId?.fullName || "Unknown Driver"}
                    </span>
                    {action.driverId?.phone && (
                      <span className="text-gray-500 ml-1">({action.driverId.phone})</span>
                    )}
                    <span className="text-gray-500 ml-2">performed</span>
                    <span className="font-medium ml-1">{action.action}</span>
                    {action.reason && (
                      <span className="text-red-600 ml-1 font-bold">
                        ({action.reason})
                      </span>
                    )}
                  </div>
                  <span className="text-gray-500">
                    {formatDate(action.timestamp)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Info */}
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {ride.otp && (
              <div>
                <p className="text-gray-500">OTP</p>
                <p className="font-bold text-lg">{ride.otp}</p>
              </div>
            )}
            <div>
              <p className="text-gray-500">Scheduled</p>
              <p className="font-medium">{ride.isScheduled ? "Yes" : "No"}</p>
            </div>
            <div>
              <p className="text-gray-500">Completed</p>
              <p className="font-medium">{ride.isCompleted ? "Yes" : "No"}</p>
            </div>
            <div>
              <p className="text-gray-500">Cancelled</p>
              <p className="font-medium">{ride.isCancelled ? "Yes" : "No"}</p>
            </div>
          </div>
        </div>

        {/* Close Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RideDetailModal;